// src/components/blog/components/BlogTopic.tsx

'use client'

import type { ReactNode } from 'react'
import Link from 'next/link'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import type { Topic } from '~/lib/blog/types'

interface BlogTopicProps {
  topic?: Topic
  size?: 'sm' | 'md' | 'lg'
  variant?: 'solid' | 'outline'
  className?: string
}

export function BlogTopic({ 
  topic,
  size = 'md',
  variant = 'solid',
  className 
}: BlogTopicProps): ReactNode {
  if (!topic) return null

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  }

  const variantClasses = {
    solid: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-500/20 dark:hover:bg-blue-900/30',
    outline: 'border border-blue-600/20 text-blue-700 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-400 dark:hover:bg-blue-900/20'
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="inline-block"
    >
      <Link
        href={`/blog/topics/${topic.slug}`}
        className={cn(
          "inline-flex items-center rounded-full font-medium transition-colors",
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
      >
        {topic.title}
      </Link>
    </motion.div>
  )
}